import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import * as S from "../css/StockAnalysis.styles";
import SearchBar from "./SearchBar";

const StockChat = () => {
  const { symbol } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const stock = location.state?.stock;
  const question = location.state?.question;
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);

  const [messagePairs, setMessagePairs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isStreamingComplete, setIsStreamingComplete] = useState(false);
  const [fullAnswer, setFullAnswer] = useState("");
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messagePairs, isStreamingComplete]);

  useEffect(() => {
    if (!stock || !question) {
      navigate(`stock/${symbol}`);
      return;
    }

    // Initialize with the first question
    setMessagePairs([
      {
        id: 1,
        question: { id: 1, text: question.text, isUser: true },
        answer: { id: 2, text: "", isUser: false },
      },
    ]);
    setIsLoading(true);
    fetchAnswer(question.id);
  }, [symbol, stock, question, navigate]);

  const simulateStreaming = (text) => {
    setIsLoading(false);
    let currentIndex = 0;
    const interval = 10;

    // Check if the text contains HTML tags
    const hasHtml = /<[^>]*>/g.test(text);

    if (hasHtml) {
      // If there's HTML, find the last HTML tag and its position
      const htmlMatch = text.match(/<[^>]*>([^<]*)$/);
      if (htmlMatch) {
        const htmlEndIndex = (htmlMatch.index ?? 0) + htmlMatch[0].length;
        const plainTextStart = htmlEndIndex;

        // Set the HTML part immediately
        setMessagePairs((prev) => {
          const newPairs = [...prev];
          const lastPair = newPairs[newPairs.length - 1];
          lastPair.answer.text = text.substring(0, plainTextStart);
          return newPairs;
        });

        // Start streaming from where the plain text begins
        currentIndex = plainTextStart;
      }
    }

    const stream = () => {
      if (currentIndex < text.length) {
        setMessagePairs((prev) => {
          const newPairs = [...prev];
          const lastPair = newPairs[newPairs.length - 1];
          lastPair.answer.text = text.substring(0, currentIndex + 1);
          return newPairs;
        });
        currentIndex++;
        scrollToBottom();
      } else {
        clearInterval(interval);
        setIsStreamingComplete(true);
      }
    };

    const intervalId = setInterval(stream, interval);
    return () => clearInterval(intervalId);
  };

  const fetchAnswer = async (questionId) => {
    setIsStreamingComplete(false);
    setFollowUpQuestions([]);
    setIsLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/analysis/answer/${symbol}/${questionId}`
      );
      const { answer, followUpQuestions } = response.data;

      if (answer && typeof answer === "string") {
        setFullAnswer(answer);
        setFollowUpQuestions(followUpQuestions);
        simulateStreaming(answer);
      } else {
        simulateStreaming("No answer received from the server.");
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching answer:", error);
      simulateStreaming("Error fetching answer. Please try again.");
    }
  };

  const handleSendQuestion = async () => {
    if (!searchQuery.trim()) return;

    // Add new message pair
    setMessagePairs((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        question: {
          id: prev.length * 2 + 1,
          text: searchQuery,
          isUser: true,
        },
        answer: {
          id: prev.length * 2 + 2,
          text: "",
          isUser: false,
        },
      },
    ]);

    setSearchQuery("");
    setIsStreamingComplete(false);
    setFollowUpQuestions([]);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/analysis/follow-up`,
        {
          symbol,
          question: searchQuery,
          questionId: question?.id,
        }
      );

      const { answer, followUpQuestions } = response.data;
      setFullAnswer(answer);
      setFollowUpQuestions(followUpQuestions);

      // Start streaming the answer
      simulateStreaming(answer);
    } catch (error) {
      setIsLoading(false);
      console.error("Error sending follow-up question:", error);
      simulateStreaming("Error sending question. Please try again.");
    }

  };

  const handleFollowUpQuestionClick = (question) => {
    setMessagePairs((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        question: {
          id: prev.length * 2 + 1,
          text: question,
          isUser: true,
        },
        answer: {
          id: prev.length * 2 + 2,
          text: "",
          isUser: false,
        },
      },
    ]);

    setIsStreamingComplete(false);
    setIsLoading(true);

    // Send the follow-up question
    axios.post(
      `${process.env.REACT_APP_API_URL}/api/analysis/follow-up`,
      {
        symbol,
        question: question,
      }
    )
      .then((response) => {
        console.log(response.data);
        setFullAnswer(response.data.answer);
        setFollowUpQuestions(response.data.followUpQuestions);
        simulateStreaming(response.data.answer);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Error sending follow-up question:", error);
        simulateStreaming("Error sending question. Please try again.");
      });

  };

  if (!stock || !question) {
    return <div>Loading...</div>;
  }

  return (
    <S.ChatContainer>
      <S.ChatHeader>
        <S.StockName>{stock.COMPNAME}</S.StockName>
      </S.ChatHeader>
      <S.ChatMessages ref={chatMessagesRef}>
        {messagePairs.map((pair) => (
          <React.Fragment key={pair.id}>
            <S.Message isUser={true}>{pair.question.text}</S.Message>
            {pair.answer.text ? (
              <S.Message
                isUser={false}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(pair.answer.text),
                }}
              />
            ) : isLoading ? (
              <S.ShimmerMessage>Thinking...</S.ShimmerMessage>
            ) : null}
          </React.Fragment>
        ))}
        {isStreamingComplete && followUpQuestions.length > 0 && (
          <S.FollowUpQuestions>
            {followUpQuestions.map((q, index) => (
              <S.FollowUpQuestionCard
                key={index}
                onClick={() => handleFollowUpQuestionClick(q)}
              >
                {q}
              </S.FollowUpQuestionCard>
            ))}
          </S.FollowUpQuestions>
        )}
        <div ref={messagesEndRef} />
      </S.ChatMessages>
      {isStreamingComplete && (
        <S.ChatInputContainer>
          <SearchBar
            placeholder="Ask a follow-up question..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            mode="chat"
            onSend={handleSendQuestion}
          />
        </S.ChatInputContainer>
      )}
    </S.ChatContainer>
  );
};

export default StockChat;
